import {MicroreelGroup} from '../../../data/models/Microreels/MicroreelGroup';
import {StoredGenerationInfo} from './generatingTypes';

// No job status endpoint: success = a new group in the list, failure = timeout.
export const GENERATION_TIMEOUT_MS = 45 * 60 * 1000;

// The backend may truncate the name.
const normalize = (value?: string) => (value ?? '').trim().toLowerCase();

const titleMatches = (info: StoredGenerationInfo, group: MicroreelGroup) => {
  const infoTitle = normalize(info.title);
  const groupTitle = normalize(group.title);
  if (!infoTitle || !groupTitle) return false;
  return infoTitle === groupTitle || infoTitle.startsWith(groupTitle);
};

export interface SettledGeneration {
  info: StoredGenerationInfo;
  groupId: string;
}

export interface GenerationMatchResult {
  finished: SettledGeneration[];
  failed: StoredGenerationInfo[];
  pending: StoredGenerationInfo[];
  // Set by settleGenerations when the poll could not reach the backend, so a
  // stalled screen can tell "still generating" apart from "we cannot tell".
  checkFailed?: boolean;
}

// groups: first page, newest first. Only a title match claims a group, taking
// one by position steals another parallel generation's result.
export const matchGenerations = (
  infos: StoredGenerationInfo[],
  groups: MicroreelGroup[],
  now: number,
): GenerationMatchResult => {
  const finished: SettledGeneration[] = [];
  const failed: StoredGenerationInfo[] = [];
  const pending: StoredGenerationInfo[] = [];
  const claimed = new Set<string>();
  const sorted = [...infos].sort((a, b) => a.startedAt - b.startedAt);

  const candidatesFor = (info: StoredGenerationInfo) => {
    if (info.previousGroupId === undefined) return groups;
    if (info.previousGroupId === null) {
      return groups.filter(g => !claimed.has(g.id));
    }
    const boundary = groups.findIndex(g => g.id === info.previousGroupId);
    const created = boundary === -1 ? groups : groups.slice(0, boundary);
    return created.filter(g => !claimed.has(g.id));
  };

  const unmatched: StoredGenerationInfo[] = [];
  sorted.forEach(info => {
    const match = candidatesFor(info).find(g => titleMatches(info, g));
    if (match) {
      claimed.add(match.id);
      finished.push({info, groupId: match.id});
    } else {
      unmatched.push(info);
    }
  });

  unmatched.forEach(info => {
    if (now - info.startedAt > GENERATION_TIMEOUT_MS) {
      failed.push(info);
    } else {
      pending.push(info);
    }
  });

  return {finished, failed, pending};
};
